import type { ReleaseType } from './VersionComparator'
import type { VersionStore } from './VersionStore'

/**
 * `automatic` — reload silencioso, sem UI nenhuma, esperando o app ficar ocioso.
 * `modal` — aviso informativo com "Atualizar agora", que nunca bloqueia nada.
 */
export type UpdateLayer = 'automatic' | 'modal'

type ReloadContext = {
  idle: boolean
  /** Troca de rota ou volta ao dashboard: momento seguro mesmo sem idle. */
  safeNavigation?: boolean
}

/**
 * Decide **se** e **como** atualizar — nunca **o que o usuário pode fazer**.
 *
 * Duas camadas, não três: `patch` e `minor` colapsaram na automática (não há
 * toast nesta política) e só `major` mostra UI. E em nenhuma delas há bloqueio
 * de ação: a alternativa exigiria manter uma lista de "ações críticas" que
 * cresce a cada feature nova, com complexidade desproporcional ao ganho.
 */
export const createUpdatePolicy = (store: VersionStore) => {
  const majorPendenteDeAviso = () => {
    const { releaseType, latestVersion, dismissedVersion } = store.getState()

    return releaseType === 'major' && latestVersion !== dismissedVersion
  }

  const policy = {
    layerFor: (releaseType: ReleaseType | null): UpdateLayer | null => {
      if (releaseType === null) return null

      return releaseType === 'major' ? 'modal' : 'automatic'
    },

    /**
     * Único portão antes de qualquer `location.reload()`, automático ou manual.
     * Só conta razões — não sabe o que é quiz, jornada ou check-in, e é por
     * isso que o próximo fluxo crítico se protege com uma linha.
     */
    canReload: () => store.getState().blockingReasons.size === 0,

    shouldShowModal: () =>
      store.getState().updateAvailable && majorPendenteDeAviso(),

    /**
     * A major que o usuário ignorou não fica pendurada: ela deixa de esperar o
     * modal e passa a ser tratada como qualquer outra atualização — reload no
     * próximo momento ocioso e desbloqueado.
     */
    shouldReloadAutomatically: ({ idle, safeNavigation = false }: ReloadContext) => {
      if (!store.getState().updateAvailable) return false
      if (majorPendenteDeAviso()) return false
      if (!policy.canReload()) return false

      return idle || safeNavigation
    }
  }

  return policy
}

export type UpdatePolicy = ReturnType<typeof createUpdatePolicy>
